import { Button } from "@/components/atoms/Button";
import { NetworkImage } from "@/components/atoms/NetworkImage";
import { PlaceholderImage } from "@/components/atoms/PlaceholderImage";
import { Typography } from "@/components/atoms/Typography";
import {
  ChevronRightIcon,
  CircleCheckBigIcon,
  TimerIconBold,
  TriangleAlertIcon,
} from "@/components/atoms/icons";
import { AdhaUnitStatusBadge } from "@/components/molecules/AdhaUnitStatusBadge";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import React from "react";
import { ImageSourcePropType, Pressable, StyleSheet, View } from "react-native";

export type PropertyCardVariant = "default" | "upcoming" | "noPending";

export interface PropertyCardProps {
  images?: ImageSourcePropType[]; // carousel images
  projectName: string; // e.g., Bloom Central
  unitTitle: string; // e.g., Flat No A1 -401, 3BHK, Abu Dhabi
  constructionCompleted?: string; // e.g., 45% (optional - only show if handoverComplete is true)
  installmentPaid: string; // e.g., 55%
  variant?: PropertyCardVariant;
  statusText?: string; // e.g., 3 Missed payments | 2 Payments due in 30 days | No pending dues
  statusCount?: number;
  statusLabel?: string;
  onPrimaryActionPress?: () => void; // e.g., Pay all
  layout?: "grid" | "list";
  onPress?: () => void;
  /** Owned ADHA unit status — chip on the photo (grid) or above the title (list). */
  adhaUnitStatus?: string | null | undefined;
}

export function PropertyCard({
  images,
  projectName,
  unitTitle,
  constructionCompleted,
  installmentPaid,
  variant = "default",
  statusText,
  statusCount,
  statusLabel,
  onPrimaryActionPress,
  layout = "grid",
  onPress,
  adhaUnitStatus,
}: PropertyCardProps) {
  // All hooks must be called unconditionally and in the same order every render
  const c = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const [isScrolling, setIsScrolling] = React.useState(false);
  const scrollTimeoutRef = React.useRef<number | null>(null);

  // useEffect must be called before any conditional returns
  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Derived values (not hooks) - can be called after hooks
  const statusBg =
    variant === "default"
      ? UI_PRIMITIVES.redLightest2
      : variant === "upcoming"
        ? UI_PRIMITIVES.yellowLightest
        : UI_PRIMITIVES.greenLighter;

  const StatusIcon =
    variant === "noPending"
      ? CircleCheckBigIcon
      : variant === "upcoming"
        ? TimerIconBold
        : TriangleAlertIcon;
  const STATUS_ICON_SIZE = 24;

  const statusContent =
    statusCount != null && statusLabel != null ? (
      <View
        style={[
          styles.statusText,
          {
            flexDirection: "row",
            justifyContent: isRTL ? "flex-end" : "flex-start",
            alignItems: "center",
          },
        ]}
      >
        <View
          style={[
            styles.statusCountLabelRow,
            { flexDirection: isRTL ? "row-reverse" : "row" },
          ]}
        >
          <Typography
            variant="headline"
            color={variant === "default" ? "error" : "primary"}
            style={styles.statusCountText}
          >
            {statusCount}
          </Typography>
          <Typography
            variant="headline"
            color="primary"
            style={[styles.statusLabelText, { flex: 0 }]}
          >
            {statusLabel}
          </Typography>
        </View>
      </View>
    ) : (
      <Typography
        variant="headline"
        color="primary"
        style={[
          styles.statusText,
          { paddingTop: isRTL ? 6 : 2, textAlign: isRTL ? "right" : "left" },
        ]}
      >
        {statusText}
      </Typography>
    );

  // Event handlers (not hooks) - can be defined after hooks
  const handleCardPress = () => {
    // Only trigger onPress if not scrolling
    if (!isScrolling && onPress) {
      // Track property selection event
      trackDynatraceEvent({
        p_screen: "Properties",
        p_actionName: "Property Selected",
        p_sectionName: "Property List",
        eventType: "action",
        projectName: projectName || "",
        unitTitle: unitTitle || "",
      });
      onPress();
    }
  };

  const handleScrollStart = () => {
    setIsScrolling(true);
    // Clear any pending timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  };

  const handleScrollEnd = () => {
    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    // Delay reset to prevent immediate press after scroll
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150) as any;
  };

  if (layout === "list") {
    const firstImage = images && images.length > 0 ? images[0] : undefined;
    const firstImageUri =
      firstImage && typeof firstImage === "object" && "uri" in firstImage
        ? firstImage.uri
        : undefined;
    return (
      <Pressable
        style={[
          styles.listContainer,
          {
            backgroundColor: c.backgroundLevel1,
            borderColor: c.backgroundLevel2,
          },
        ]}
        onPress={onPress}
      >
        <View style={styles.listRow}>
          <View style={styles.listImageWrap}>
            {firstImageUri ? (
              <NetworkImage
                url={firstImageUri}
                style={styles.listImage}
                resizeMode="cover"
              />
            ) : (
              <View
                style={[
                  styles.listImagePlaceholder,
                  { backgroundColor: c.backgroundLevel2 },
                ]}
              >
                <PlaceholderImage />
              </View>
            )}
          </View>
          <View style={[styles.content, styles.listContent]}>
            {adhaUnitStatus ? (
              <AdhaUnitStatusBadge rawStatus={adhaUnitStatus} placement="listing" />
            ) : null}
            <View style={{ gap: 4, alignItems: "flex-start" }}>
              <Typography
                variant="headline"
                color="primary"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {unitTitle}
              </Typography>
            </View>
            <View
              style={[
                styles.metaRow,
                isRTL && { flexDirection: "row-reverse" },
              ]}
            >
              <View style={{ flex: 1, gap: 4, alignItems: "flex-start" }}>
                <Typography variant="subhead" color="primary">
                  {installmentPaid}
                </Typography>
                <Typography
                  variant="footnote"
                  color="tertiary"
                  style={{ textAlign: isRTL ? "right" : "left" }}
                >
                  {t("properties.installmentPaid")}
                </Typography>
              </View>
              {constructionCompleted && (
                <View style={{ flex: 1, gap: 4, alignItems: "flex-start" }}>
                  <Typography variant="subhead" color="primary">
                    {constructionCompleted}
                  </Typography>
                  <Typography
                    variant="footnote"
                    color="tertiary"
                    style={{ textAlign: isRTL ? "right" : "left" }}
                  >
                    {t("properties.constructionCompleted")}
                  </Typography>
                </View>
              )}
            </View>
          </View>
        </View>
        {variant !== "noPending" && onPrimaryActionPress ? (
          <Pressable
            style={[
              styles.listStatusBar,
              {
                backgroundColor: statusBg,
                paddingLeft: 12,
                paddingRight: 12,
                flexDirection: "row",
                direction: isRTL ? "rtl" : "ltr",
              },
            ]}
            onPress={onPrimaryActionPress}
          >
            <View style={styles.iconContainer}>
              <StatusIcon size={STATUS_ICON_SIZE} color={c.fontPrimary} />
            </View>
            {statusContent}
            <Button
              title={t("payments.pay")}
              onPress={onPrimaryActionPress}
              style={styles.payAllBtn}
              variant="text"
              trailing={
                <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                  <ChevronRightIcon size={18} color={c.fontPrimary} />
                </View>
              }
            />
          </Pressable>
        ) : (
          <View
            style={[
              styles.listStatusBar,
              {
                backgroundColor: statusBg,
                paddingLeft: 12,
                paddingRight: 12,
                flexDirection: "row",
                direction: isRTL ? "rtl" : "ltr",
              },
            ]}
          >
            <View style={styles.iconContainer}>
              <StatusIcon size={STATUS_ICON_SIZE} color={c.fontPrimary} />
            </View>
            {statusContent}
          </View>
        )}
      </Pressable>
    );
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: c.backgroundLevel1,
          borderColor: c.backgroundLevel2,
        },
      ]}
    >
      <View style={styles.imageWrap}>
        <ImageCarousel
          images={images ?? []}
          height={200}
          onScrollStart={handleScrollStart}
          onScrollEnd={handleScrollEnd}
          onPress={handleCardPress}
        />
        {adhaUnitStatus ? (
          <View
            pointerEvents="none"
            style={[
              styles.adhaStatusOverlay,
              isRTL ? styles.adhaStatusOverlayRTL : null,
            ]}
          >
            <AdhaUnitStatusBadge rawStatus={adhaUnitStatus} placement="listing" />
          </View>
        ) : null}
      </View>

      <Pressable onPress={handleCardPress} style={styles.content}>
        <View style={{ gap: 4, alignItems: "flex-start" }}>
          <Typography
            variant="headline"
            color="primary"
            style={{ textAlign: isRTL ? "right" : "left" }}
          >
            {unitTitle}
          </Typography>
        </View>

        <View
          style={[styles.metaRow, isRTL && { flexDirection: "row-reverse" }]}
        >
          <View style={{ flex: 1, gap: 4, alignItems: "flex-start" }}>
            <Typography variant="subhead" color="primary">
              {installmentPaid}
            </Typography>
            <Typography
              variant="footnote"
              color="tertiary"
              style={{ textAlign: isRTL ? "right" : "left" }}
            >
              {t("properties.installmentPaid")}
            </Typography>
          </View>
          {constructionCompleted && (
            <View style={{ flex: 1, gap: 4, alignItems: "flex-start" }}>
              <Typography variant="subhead" color="primary">
                {constructionCompleted}
              </Typography>
              <Typography
                variant="footnote"
                color="tertiary"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("properties.constructionCompleted")}
              </Typography>
            </View>
          )}
        </View>

        {variant !== "noPending" && onPrimaryActionPress ? (
          <Pressable
            style={[
              styles.statusBar,
              {
                backgroundColor: statusBg,
                paddingLeft: 12,
                paddingRight: 12,
                flexDirection: "row",
                direction: isRTL ? "rtl" : "ltr",
              },
            ]}
            onPress={onPrimaryActionPress}
          >
            <View style={styles.iconContainer}>
              <StatusIcon size={STATUS_ICON_SIZE} color={c.fontPrimary} />
            </View>
            {statusContent}
            <Button
              title={t("payments.pay")}
              variant="text"
              onPress={onPrimaryActionPress}
              style={styles.payAllBtn}
              trailing={
                <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                  <ChevronRightIcon size={18} color={c.fontPrimary} />
                </View>
              }
            />
          </Pressable>
        ) : (
          <View
            style={[
              styles.statusBar,
              {
                backgroundColor: statusBg,
                paddingLeft: 12,
                paddingRight: 12,
                flexDirection: "row",
                direction: isRTL ? "rtl" : "ltr",
              },
            ]}
          >
            <View style={styles.iconContainer}>
              <StatusIcon size={STATUS_ICON_SIZE} color={c.fontPrimary} />
            </View>
            {statusContent}
          </View>
        )}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    padding: 8,
    gap: 4,
    borderWidth: 1,
  },
  listContainer: {
    borderRadius: 12,
    padding: 12,
    gap: 16,
    borderWidth: 1,
  },
  listRow: {
    flexDirection: "row",
    gap: 12,
  },
  imageWrap: {
    height: 230,
    borderRadius: 8,
    overflow: "hidden",
  },
  adhaStatusOverlay: {
    position: "absolute",
    top: 12,
    left: 12,
    right: 12,
    zIndex: 2,
    alignItems: "flex-start",
  },
  adhaStatusOverlayRTL: {
    alignItems: "flex-end",
  },
  listImageWrap: {
    width: 107,
    borderRadius: 12,
    overflow: "hidden",
    aspectRatio: 1, // Square aspect ratio
  },
  listImage: {
    width: "100%",
    height: "100%",
  },
  listImagePlaceholder: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  content: {
    gap: 16,
  },
  listContent: {
    flex: 1,
    minWidth: 0,
  },
  metaRow: {
    flexDirection: "row",
    gap: 16,
  },
  statusBar: {
    flexDirection: "row",
    alignItems: "center",
    height: 36,
    gap: 8,
    borderRadius: 8,
  },
  listStatusBar: {
    flexDirection: "row",
    alignItems: "center",
    height: 36,
    gap: 8,
    borderRadius: 8,
  },
  statusText: {
    flex: 1,
  },
  statusCountLabelRow: {
    gap: 6,
    alignItems: "center",
  },
  statusCountText: {
    flexShrink: 0,
  },
  statusLabelText: {
    flex: 1,
  },
  iconContainer: {
    width: 24,
    height: 24,
    minWidth: 24,
    minHeight: 24,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  payAllBtn: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    minHeight: 0,
    alignSelf: "center",
  },
});

export default PropertyCard;
